import type { Notification, FocusMode, NotificationChannel } from '@/types/notifications';

/**
 * Focus Mode Filtering Engine
 * 
 * This module provides notification filtering logic based on active focus modes.
 * It's designed to integrate seamlessly with the existing NotificationManager
 * without breaking any current functionality.
 * 
 * @example
 * ```typescript
 * const result = shouldDeliverNotification(notification, activeFocusMode, channel);
 * if (result.allowed) {
 *   // Deliver notification normally
 * } else {
 *   // Log as blocked and skip delivery
 * }
 * ```
 */

export interface FilterResult {
  allowed: boolean;
  reason?: string;
  blockReason?: 'dnd' | 'not_in_allowlist' | 'in_blocklist' | 'channel_disabled';
}

/**
 * Determines if a notification should be delivered based on the active focus mode
 * 
 * @param notification - The notification to evaluate
 * @param activeFocusMode - The currently active focus mode (null if none)
 * @param channel - The notification channel (optional, for channel-based filtering)
 * @returns FilterResult indicating whether to deliver and why
 */
export function shouldDeliverNotification(
  notification: Notification,
  activeFocusMode: FocusMode | null,
  channel?: NotificationChannel | null
): FilterResult {
  // No active focus mode = allow all notifications
  if (!activeFocusMode) {
    return { allowed: true };
  }

  // If notification priority is urgent, it always bypasses focus mode
  // This ensures critical alerts are never blocked
  if (notification.priority === 'urgent') {
    return { 
      allowed: true, 
      reason: 'Urgent priority bypasses focus mode' 
    };
  }

  const channelId = notification.channel_id || channel?.id;

  // Do Not Disturb mode: very strict filtering
  if (activeFocusMode.do_not_disturb) {
    // Check if channel has DND override permission
    if (channel?.do_not_disturb_override) {
      return { 
        allowed: true, 
        reason: 'Channel has Do Not Disturb override' 
      };
    }

    // Check if channel is explicitly allowed in DND mode
    if (channelId && activeFocusMode.allowed_channels?.includes(channelId)) {
      return { 
        allowed: true, 
        reason: 'Channel allowed in Do Not Disturb mode' 
      };
    }

    // Block everything else in DND mode
    return { 
      allowed: false, 
      reason: 'Blocked by Do Not Disturb mode',
      blockReason: 'dnd'
    };
  }

  // Regular focus mode: check allowlist and blocklist
  
  // If allowlist is set (non-empty), only allow channels in the list
  if (activeFocusMode.allowed_channels && activeFocusMode.allowed_channels.length > 0) {
    if (!channelId || !activeFocusMode.allowed_channels.includes(channelId)) {
      return { 
        allowed: false, 
        reason: `Channel not in allowed list for ${activeFocusMode.name}`,
        blockReason: 'not_in_allowlist'
      };
    }
  }

  // Check blocklist (has lower priority than allowlist)
  if (channelId && activeFocusMode.blocked_channels?.includes(channelId)) {
    return { 
      allowed: false, 
      reason: `Channel blocked by ${activeFocusMode.name}`,
      blockReason: 'in_blocklist'
    };
  }

  // Check if the channel itself is disabled
  if (channel && !channel.enabled) {
    return {
      allowed: false,
      reason: 'Channel is disabled',
      blockReason: 'channel_disabled'
    };
  }

  // Passed all filters - allow delivery
  return { 
    allowed: true,
    reason: 'Notification passes focus mode filters'
  };
}

/**
 * Checks if the current time falls within any of the focus mode's scheduled times
 * 
 * @param focusMode - The focus mode to check
 * @param currentTime - The current time (defaults to now)
 * @returns boolean indicating if the mode should be active based on schedule
 */
export function shouldFocusModeBeActive(
  focusMode: FocusMode,
  currentTime: Date = new Date()
): boolean {
  // If manually activated, respect that
  if (focusMode.is_active) {
    return true;
  }

  // No schedules = not scheduled to be active
  if (!focusMode.active_times || focusMode.active_times.length === 0) {
    return false;
  }

  const now = currentTime;
  const currentDay = now.getDay(); // 0-6 (Sunday-Saturday)
  const currentTimeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  // Check if current time matches any schedule
  for (const schedule of focusMode.active_times) {
    // Check if today is in the schedule's days
    if (!schedule.days.includes(currentDay)) {
      continue;
    }

    // Check if current time is within the schedule's time range
    if (currentTimeStr >= schedule.start && currentTimeStr <= schedule.end) {
      return true;
    }
  }

  return false;
}

/**
 * Gets a human-readable summary of what notifications a focus mode will block
 * 
 * @param focusMode - The focus mode to summarize
 * @param allChannels - All available channels
 * @returns Array of summary strings
 */
export function getFocusModeSummary(
  focusMode: FocusMode,
  allChannels: NotificationChannel[]
): string[] {
  const summary: string[] = [];

  if (focusMode.do_not_disturb) {
    summary.push('🌙 Do Not Disturb enabled');
    
    const allowedChannelNames = allChannels
      .filter(c => focusMode.allowed_channels?.includes(c.id))
      .map(c => c.name);
    
    if (allowedChannelNames.length > 0) {
      summary.push(`✅ Only allowing: ${allowedChannelNames.join(', ')}`);
    } else {
      summary.push('🔕 Blocking all notifications except urgent');
    }
  } else {
    if (focusMode.allowed_channels && focusMode.allowed_channels.length > 0) {
      const allowedChannelNames = allChannels
        .filter(c => focusMode.allowed_channels?.includes(c.id))
        .map(c => c.name);
      summary.push(`✅ Allowing: ${allowedChannelNames.join(', ')}`);
    }

    if (focusMode.blocked_channels && focusMode.blocked_channels.length > 0) {
      const blockedChannelNames = allChannels
        .filter(c => focusMode.blocked_channels?.includes(c.id))
        .map(c => c.name);
      summary.push(`🚫 Blocking: ${blockedChannelNames.join(', ')}`);
    }

    if (
      (!focusMode.allowed_channels || focusMode.allowed_channels.length === 0) &&
      (!focusMode.blocked_channels || focusMode.blocked_channels.length === 0)
    ) {
      summary.push('📬 Allowing all notifications');
    }
  }

  // Add schedule info
  if (focusMode.active_times && focusMode.active_times.length > 0) {
    const scheduleStr = focusMode.active_times
      .map(s => `${getDayNames(s.days)}: ${s.start}-${s.end}`)
      .join('; ');
    summary.push(`⏰ Scheduled: ${scheduleStr}`);
  }

  return summary;
}

/**
 * Helper to convert day numbers to readable names
 */
function getDayNames(days: number[]): string {
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  // Check for common patterns
  if (days.length === 7) return 'Every day';
  if (days.length === 5 && days.every(d => d >= 1 && d <= 5)) return 'Weekdays';
  if (days.length === 2 && days.includes(0) && days.includes(6)) return 'Weekends';
  
  return days.map(d => dayNames[d]).join(', ');
}

/**
 * Calculates statistics about blocked notifications
 * 
 * @param notifications - All notifications
 * @param focusMode - The focus mode that was active
 * @param channels - All channels
 * @returns Statistics object
 */
export function calculateBlockedStats(
  notifications: Notification[],
  focusMode: FocusMode,
  channels: NotificationChannel[]
): {
  totalBlocked: number;
  blockedByDnd: number;
  blockedByAllowlist: number;
  blockedByBlocklist: number;
  topBlockedChannels: { channelName: string; count: number }[];
} {
  const blocked = notifications.filter(n => {
    const channel = channels.find(c => c.id === n.channel_id);
    const result = shouldDeliverNotification(n, focusMode, channel);
    return !result.allowed;
  });

  const stats = {
    totalBlocked: blocked.length,
    blockedByDnd: blocked.filter(n => {
      const channel = channels.find(c => c.id === n.channel_id);
      const result = shouldDeliverNotification(n, focusMode, channel);
      return result.blockReason === 'dnd';
    }).length,
    blockedByAllowlist: blocked.filter(n => {
      const channel = channels.find(c => c.id === n.channel_id);
      const result = shouldDeliverNotification(n, focusMode, channel);
      return result.blockReason === 'not_in_allowlist';
    }).length,
    blockedByBlocklist: blocked.filter(n => {
      const channel = channels.find(c => c.id === n.channel_id);
      const result = shouldDeliverNotification(n, focusMode, channel);
      return result.blockReason === 'in_blocklist';
    }).length,
    topBlockedChannels: [] as { channelName: string; count: number }[],
  };

  // Calculate top blocked channels
  const channelCounts = new Map<string, number>();
  blocked.forEach(n => {
    if (n.channel_id) {
      const count = channelCounts.get(n.channel_id) || 0;
      channelCounts.set(n.channel_id, count + 1);
    }
  });

  stats.topBlockedChannels = Array.from(channelCounts.entries())
    .map(([channelId, count]) => ({
      channelName: channels.find(c => c.id === channelId)?.name || 'Unknown',
      count,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  return stats;
}
